<script setup lang="ts">
import DropDown, { type DropDownItem } from '@/components/common/DropDown.vue';
import { financialConverter } from '@/converters/financials/financials-converter';
import { adminSupplierService } from '@/services/admin/admin-supplier-service.ts';
import { overlaySpinnerService } from '@/services/app/overlay-spinner-service.ts';
import { notificationService } from '@/services/notification/notification-service';
import { FinancialConverterError } from '@/utils/errors/converters/financial-converter-errors';
import { models } from 'cesg-shared';
import { VatType } from 'cesg-shared/dist/models';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AdminSupplierInvoice from '../../supplier-invoice/AdminSupplierInvoice.vue';

const vatTypeOptions = computed<DropDownItem[]>(() =>
  Object.values(VatType).map((value) => ({
    value,
    text: t(`adminCreateInvoice.bottomRows.vatTypes.${value}`),
  })),
);

const currencyOptions = computed<{ value: string; text: string }[]>(() => [
  {
    value: 'SEK',
    text: 'SEK',
  },
]);

const getVatTypeText = (vatType: string | null | undefined): string => {
  if (!vatType) return t('adminSupplier.detailsBasicInfo.notAvailable');
  const option = vatTypeOptions.value.find((opt) => opt.value === vatType);
  return option ? option.text : vatType;
};

const { t } = useI18n();

const emit = defineEmits<{
  (e: 'deleted', id: string): void;
  (e: 'created', id: string): void;
  (e: 'cancel-create'): void;
  (e: 'updated', id: string): void;
  (e: 'ready'): void;
}>();

const props = defineProps<{
  supplierId: string | null;
  largeFont?: boolean;
  createMode?: boolean;
}>();

const currentSupplier = ref<models.AdminSupplierDetailResponse | null>(null);
const isEditing = ref(false);
const formData = ref<Partial<models.AdminSupplierDetailResponse>>({});
const showDeleteModal = ref(false);

const toReadableSupplierData = <
  T extends Partial<models.AdminSupplierDetailResponse>,
>(
  data: T,
): T => {
  return {
    ...data,
    organisationNumber: data.organisationNumber
      ? financialConverter.readableOrgNr(data.organisationNumber)
      : data.organisationNumber,
    bankGiro: data.bankGiro
      ? financialConverter.readableBankgiro(data.bankGiro)
      : data.bankGiro,
    postGiro: data.postGiro
      ? financialConverter.readablePlusgiro(data.postGiro)
      : data.postGiro,
    zipCode: data.zipCode
      ? financialConverter.readableZipCode(data.zipCode)
      : data.zipCode,
    bic: data.bic ? financialConverter.readableBIC(data.bic) : data.bic,
    iban: data.iban ? financialConverter.readableIBAN(data.iban) : data.iban,
  };
};

const isInputMode = computed(() => isEditing.value || props.createMode);

const promptDelete = () => {
  showDeleteModal.value = true;
};

const loadSupplierDetails = async (id: string | null) => {
  if (!id) {
    currentSupplier.value = null;
    emit('ready');
    return;
  }

  if (currentSupplier.value && currentSupplier.value.id === id) {
    isEditing.value = false;
    emit('ready');
    return;
  }

  overlaySpinnerService.show();
  currentSupplier.value = null;
  isEditing.value = false;

  try {
    const result = await adminSupplierService.getDetails(id);
    if (result) {
      currentSupplier.value = toReadableSupplierData(result);
    }
  } catch (error) {
    console.error(`Failed to load details for supplier ${id}`, error);
    currentSupplier.value = null;
  } finally {
    overlaySpinnerService.hide();
    emit('ready');
  }
};

const startEdit = () => {
  if (!currentSupplier.value) return;
  formData.value = JSON.parse(JSON.stringify(currentSupplier.value));
  formData.value.currency = 'SEK';
  isEditing.value = true;
};

const cancelEditOrCreate = () => {
  if (props.createMode) {
    emit('cancel-create');
  } else {
    isEditing.value = false;
    formData.value = {};
  }
};

const handleSave = async () => {
  if (props.createMode) {
    await saveNewSupplier();
  } else {
    await saveUpdate();
  }
};

const validatePaymentFields = () => {
  const hasValue = (val: string | null | undefined) =>
    val !== null && val !== undefined && val.trim() !== '';

  const bankGiroValue = formData.value.bankGiro;
  const postGiroValue = formData.value.postGiro;

  const hasBankGiro = hasValue(bankGiroValue);
  const hasPostGiro = hasValue(postGiroValue);
  const hasIban = hasValue(formData.value.iban);
  const hasBic = hasValue(formData.value.bic);

  return hasBankGiro || hasPostGiro || (hasIban && hasBic);
};

const validateAllFields = () => {
  const hasValue = (val: string | null | undefined) =>
    val !== null && val !== undefined && val.trim() !== '';

  // Check organisationNumber
  if (!hasValue(formData.value.organisationNumber)) {
    notificationService.open({
      text: t('adminSupplier.validation.organisationNumberRequired'),
      type: 'danger',
    });
    return false;
  }

  // Check supplier name
  if (!hasValue(formData.value.name)) {
    notificationService.open({
      text: t('adminSupplier.validation.supplierNameRequired'),
      type: 'danger',
    });
    return false;
  }

  // Check streetName
  if (!hasValue(formData.value.streetName)) {
    notificationService.open({
      text: t('adminSupplier.validation.streetNameRequired'),
      type: 'danger',
    });
    return false;
  }

  // Check buildingNumber
  if (!hasValue(formData.value.buildingNumber)) {
    notificationService.open({
      text: t('adminSupplier.validation.addressRequired'),
      type: 'danger',
    });
    return false;
  }

  // Check zipCode
  if (!hasValue(formData.value.zipCode)) {
    notificationService.open({
      text: t('adminSupplier.validation.zipCodeRequired'),
      type: 'danger',
    });
    return false;
  }

  // Check city
  if (!hasValue(formData.value.city)) {
    notificationService.open({
      text: t('adminSupplier.validation.cityRequired'),
      type: 'danger',
    });
    return false;
  }

  // Check vatNumber
  if (!hasValue(formData.value.vatNumber)) {
    notificationService.open({
      text: t('adminSupplier.validation.vatNumberRequired'),
      type: 'danger',
    });
    return false;
  }

  // Check currency
  if (!hasValue(formData.value.currency)) {
    notificationService.open({
      text: t('adminSupplier.validation.currencyRequired'),
      type: 'danger',
    });
    return false;
  }

  return true;
};

const saveNewSupplier = async () => {
  if (!formData.value) return;

  if (!validateAllFields()) {
    return;
  }

  if (!validatePaymentFields()) {
    notificationService.open({
      text: t('adminSupplier.validation.bankGiroOrPlusGiroRequired'),
      type: 'danger',
    });
    return;
  }

  overlaySpinnerService.show();
  try {
    const result = await adminSupplierService.createSupplier(
      formData.value as models.AdminCreateSupplierDetailResponse,
    );

    if (result) {
      emit('created', result.id);
    } else {
      notificationService.open({
        text: t('adminSupplier.modalWarning.organisationNumberExists'),
        type: 'danger',
      });
    }
  } catch (error) {
    if (error instanceof FinancialConverterError) {
      const key = `financialConverter.errors.${error.type}`;
      notificationService.open({
        type: 'danger',
        text: `${t(`${key}.message`)} (${t(`${key}.format`)})`,
      });
    } else {
      notificationService.open({
        text: t('notifications.createUnsuccessful'),
        type: 'danger',
      });
    }
  } finally {
    overlaySpinnerService.hide();
  }
};

const saveUpdate = async () => {
  if (!props.supplierId || !formData.value) return;

  if (!validateAllFields()) {
    return;
  }

  if (!validatePaymentFields()) {
    notificationService.open({
      text: t('adminSupplier.validation.bankGiroOrPlusGiroRequired'),
      type: 'danger',
    });
    return;
  }

  overlaySpinnerService.show();
  try {
    if (!formData.value.name) formData.value.name = '';

    const result = await adminSupplierService.updateSupplier(
      props.supplierId,
      formData.value as models.AdminCreateSupplierDetailResponse,
    );

    if (result) {
      currentSupplier.value = toReadableSupplierData(result);
      isEditing.value = false;
      emit('updated', props.supplierId);
    } else {
      notificationService.open({
        text: t('adminSupplier.modalWarning.organisationNumberExists'),
        type: 'danger',
      });
    }
  } catch (error) {
    if (error instanceof FinancialConverterError) {
      const key = `financialConverter.errors.${error.type}`;
      notificationService.open({
        type: 'danger',
        text: `${t(`${key}.message`)} (${t(`${key}.format`)})`,
      });
    } else {
      console.error('Failed to save supplier details', error);
      notificationService.open({
        text: t('notifications.failedToUpdateSupplier'),
        type: 'danger',
      });
    }
  } finally {
    overlaySpinnerService.hide();
  }
};

const confirmDelete = async () => {
  showDeleteModal.value = false;
  if (!props.supplierId) return;

  overlaySpinnerService.show();

  try {
    await adminSupplierService.deleteSupplier(props.supplierId);

    emit('deleted', props.supplierId);
    currentSupplier.value = null;
  } catch (error) {
    console.error('Delete error', error);

    notificationService.open({
      text: t('notifications.removeUnsuccessful'),
      type: 'danger',
    });
  } finally {
    overlaySpinnerService.hide();
  }
};

const resetForm = () => {
  currentSupplier.value = null;
  isEditing.value = false;
  formData.value = { isActive: true, countryCode: 'SE', currency: 'SEK' };
  emit('ready');
};

watch(
  [() => props.supplierId, () => props.createMode],
  ([newId, isCreating]) => {
    if (isCreating) {
      resetForm();
    } else if (newId) {
      loadSupplierDetails(newId);
    } else {
      emit('ready');
    }
  },
  { immediate: true },
);
</script>

<template>
  <form class="supplier-details-box" @submit.prevent="handleSave">
    <template v-if="currentSupplier || createMode">
      <div class="top-right-actions">
        <div class="buttons are-small">
          <template v-if="!isInputMode">
            <button type="button" @click="startEdit" class="button btn-edit">
              {{ t('adminSupplier.button.edit') }}
            </button>

            <button
              type="button"
              @click="promptDelete"
              class="button btn-delete"
              style="margin-right: 0.5rem"
            >
              {{ t('adminSupplier.button.delete') }}
            </button>
          </template>

          <template v-else>
            <button type="submit" class="button btn-save">
              {{ t('adminSupplier.button.save') }}
            </button>
            <button
              type="button"
              @click="cancelEditOrCreate"
              class="button btn-cancel"
            >
              {{ t('adminSupplier.button.cancel') }}
            </button>
          </template>
        </div>
      </div>

      <div class="details-section">
        <h3 class="title is-5">
          {{
            createMode
              ? t('adminSupplierRegister.title')
              : t('adminSupplier.detailsBasicInfo.basicInfo')
          }}
        </h3>

        <div class="three-col-grid">
          <p class="grid-item">
            <span class="label">{{
              t('adminSupplier.detailsBasicInfo.supplierNumber')
            }}</span>
            <span class="value">{{
              createMode ? '-' : currentSupplier?.supplierNumber
            }}</span>
          </p>

          <p class="grid-item">
            <span class="label"
              >{{
                t('adminSupplier.detailsBasicInfo.organisationNumber')
              }}
              *</span
            >
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.organisationNumber
            }}</span>
            <input
              v-else
              v-model="formData.organisationNumber"
              type="text"
              class="input edit-input"
            />
          </p>

          <p class="grid-item">
            <span class="label">{{
              t('adminSupplier.detailsBasicInfo.isActive')
            }}</span>
            <span
              v-if="!isInputMode"
              class="value"
              :class="{
                'is-active': currentSupplier?.isActive,
                'is-inactive': !currentSupplier?.isActive,
              }"
            >
              {{
                currentSupplier?.isActive
                  ? t('adminSupplier.detailsBasicInfo.isActiveYes')
                  : t('adminSupplier.detailsBasicInfo.isActiveNo')
              }}
            </span>
            <label v-else class="checkbox" style="margin-top: 0.25rem">
              <input type="checkbox" v-model="formData.isActive" />
              <span class="checkbox-space">
                {{
                  formData.isActive
                    ? t('adminSupplier.detailsBasicInfo.isActiveYes')
                    : t('adminSupplier.detailsBasicInfo.isActiveNo')
                }}
              </span>
            </label>
          </p>

          <p class="grid-item">
            <span class="label"
              >{{ t('adminSupplier.detailsBasicInfo.supplierName') }} *</span
            >
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.name
            }}</span>
            <input
              v-else
              v-model="formData.name"
              type="text"
              class="input edit-input"
            />
          </p>

          <p class="grid-item">
            <span class="label">{{
              t('adminSupplier.detailsBasicInfo.phone')
            }}</span>
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.phone
            }}</span>
            <input
              v-else
              v-model="formData.phone"
              type="text"
              class="input edit-input"
            />
          </p>

          <p class="grid-item">
            <span class="label">{{
              t('adminSupplier.detailsBasicInfo.email')
            }}</span>
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.email
            }}</span>
            <input
              v-else
              v-model="formData.email"
              type="text"
              class="input edit-input"
            />
          </p>

          <p class="grid-item">
            <span class="label"
              >{{ t('adminSupplier.detailsBasicInfo.streetName') }} *</span
            >
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.streetName
            }}</span>
            <input
              v-else
              v-model="formData.streetName"
              type="text"
              class="input edit-input"
            />
          </p>

          <p class="grid-item">
            <span class="label"
              >{{ t('adminSupplier.detailsBasicInfo.buildingNumber') }} *</span
            >
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.buildingNumber
            }}</span>
            <input
              v-else
              v-model="formData.buildingNumber"
              type="text"
              class="input edit-input"
            />
          </p>

          <p class="grid-item">
            <span class="label"
              >{{ t('adminSupplier.detailsBasicInfo.zipCode') }} *</span
            >
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.zipCode
            }}</span>
            <input
              v-else
              v-model="formData.zipCode"
              type="text"
              class="input edit-input"
            />
          </p>

          <p class="grid-item">
            <span class="label"
              >{{ t('adminSupplier.detailsBasicInfo.city') }} *</span
            >
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.city
            }}</span>
            <input
              v-else
              v-model="formData.city"
              type="text"
              class="input edit-input"
            />
          </p>

          <p class="grid-item">
            <span class="label">{{
              t('adminSupplier.detailsBasicInfo.country')
            }}</span>
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.country
            }}</span>
            <input
              v-else
              v-model="formData.country"
              type="text"
              class="input edit-input"
            />
          </p>

          <p class="grid-item">
            <span class="label">{{
              t('adminSupplier.detailsBasicInfo.countryCode')
            }}</span>
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.countryCode
            }}</span>
            <input
              v-else
              v-model="formData.countryCode"
              type="text"
              class="input edit-input"
            />
          </p>
          <div class="grid-spacer"></div>
        </div>
      </div>
      <hr class="strong-hr" />

      <div class="details-section">
        <h3 class="title is-5">
          {{ t('adminSupplier.detailsFinancials.financials') }}
        </h3>

        <p v-if="isInputMode" class="helper-text">
          * {{ t('adminSupplier.detailsFinancials.bgPgRequirement') }}
        </p>

        <div class="four-col-grid">
          <p class="grid-item">
            <span class="label">{{
              t('adminSupplier.detailsFinancials.bankGiro')
            }}</span>
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.bankGiro ||
              t('adminSupplier.detailsBasicInfo.notAvailable')
            }}</span>
            <input
              v-else
              v-model="formData.bankGiro"
              type="text"
              class="input edit-input"
            />
          </p>
          <p class="grid-item">
            <span class="label">{{
              t('adminSupplier.detailsFinancials.plusGiro')
            }}</span>
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.postGiro ||
              t('adminSupplier.detailsBasicInfo.notAvailable')
            }}</span>
            <input
              v-else
              v-model="formData.postGiro"
              type="text"
              class="input edit-input"
            />
          </p>
          <p class="grid-item">
            <span class="label">{{
              t('adminSupplier.detailsFinancials.bic')
            }}</span>
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.bic ||
              t('adminSupplier.detailsBasicInfo.notAvailable')
            }}</span>
            <input
              v-else
              v-model="formData.bic"
              type="text"
              class="input edit-input"
            />
          </p>
          <p class="grid-item">
            <span class="label">{{
              t('adminSupplier.detailsFinancials.iban')
            }}</span>
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.iban ||
              t('adminSupplier.detailsBasicInfo.notAvailable')
            }}</span>
            <input
              v-else
              v-model="formData.iban"
              type="text"
              class="input edit-input"
            />
          </p>

          <p class="grid-item">
            <span class="label">{{
              t('adminSupplier.detailsFinancials.accountType')
            }}</span>
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.accountType ||
              t('adminSupplier.detailsBasicInfo.notAvailable')
            }}</span>
            <input
              v-else
              v-model="formData.accountType"
              type="text"
              class="input edit-input"
            />
          </p>
          <p class="grid-item">
            <span class="label">{{
              t('adminSupplier.detailsFinancials.bankName')
            }}</span>
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.bankName ||
              t('adminSupplier.detailsBasicInfo.notAvailable')
            }}</span>
            <input
              v-else
              v-model="formData.bankName"
              type="text"
              class="input edit-input"
            />
          </p>
          <p class="grid-item">
            <span class="label">{{
              t('adminSupplier.detailsFinancials.clearingNumber')
            }}</span>
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.clearingNumber ||
              t('adminSupplier.detailsBasicInfo.notAvailable')
            }}</span>
            <input
              v-else
              v-model="formData.clearingNumber"
              type="text"
              class="input edit-input"
            />
          </p>
          <p class="grid-item">
            <span class="label">{{
              t('adminSupplier.detailsFinancials.accountNumber')
            }}</span>
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.accountNumber ||
              t('adminSupplier.detailsBasicInfo.notAvailable')
            }}</span>
            <input
              v-else
              v-model="formData.accountNumber"
              type="text"
              class="input edit-input"
            />
          </p>
        </div>
      </div>
      <hr class="strong-hr" />

      <div class="details-section">
        <h3 class="title is-5">
          {{ t('adminSupplier.detailsAccounting.accounting') }}
        </h3>

        <div class="four-col-grid">
          <p class="grid-item">
            <span class="label">{{
              t('adminSupplier.detailsAccounting.defaultAccount')
            }}</span>
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.defaultAccount ||
              t('adminSupplier.detailsBasicInfo.notAvailable')
            }}</span>
            <input
              v-else
              v-model="formData.defaultAccount"
              type="text"
              class="input edit-input"
            />
          </p>
          <p class="grid-item">
            <span class="label"
              >{{ t('adminSupplier.detailsAccounting.vatNumber') }} *</span
            >
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.vatNumber ||
              t('adminSupplier.detailsBasicInfo.notAvailable')
            }}</span>
            <input
              v-else
              v-model="formData.vatNumber"
              type="text"
              class="input edit-input"
            />
          </p>
          <div class="grid-spacer"></div>
          <div class="grid-spacer"></div>

          <p class="grid-item">
            <span class="label">{{
              t('adminSupplier.detailsAccounting.vatType')
            }}</span>
            <span v-if="!isInputMode" class="value">{{
              getVatTypeText(currentSupplier?.vatType)
            }}</span>
            <DropDown
              v-else
              :items="vatTypeOptions"
              v-model="formData.vatType"
              :label="t('adminCreateInvoice.chooseVatType')"
            />
          </p>
          <p class="grid-item">
            <span class="label"
              >{{ t('adminSupplier.detailsAccounting.currency') }} *</span
            >
            <span v-if="!isInputMode" class="value">{{
              currentSupplier?.currency ||
              t('adminSupplier.detailsBasicInfo.notAvailable')
            }}</span>
            <DropDown
              v-else
              :items="currencyOptions"
              v-model="formData.currency"
              :label="t('adminCreateInvoice.chooseCurrency')"
            />
          </p>
          <div class="grid-spacer"></div>
          <div class="grid-spacer"></div>
        </div>
      </div>

      <template v-if="!createMode">
        <hr class="strong-hr" />

        <div
          class="small-supplier-invoice"
          :class="{ 'large-font': largeFont }"
        >
          <h3 class="title is-5">
            {{ t('adminSupplier.detailsBusinessEvent.businessEvent') }}
          </h3>
          <form @submit.prevent>
            <AdminSupplierInvoice
              :filter="{
                supplier: props.supplierId || undefined,
              }"
              :hide="{
                mailboxSync: true,
                invoiceProcessing: true,
                fortnoxAccountHandler: true,
              }"
            />
          </form>
        </div>

        <div v-if="showDeleteModal" class="modal-overlay">
          <div class="modal-box">
            <h3 class="modal-title">
              {{ t('adminSupplier.modalWarning.deleteSupplier') }}
            </h3>
            <p class="modal-text">
              {{ t('adminSupplier.modalWarning.deleteSupplier') }}
              <strong>{{ currentSupplier?.name }}</strong
              >?
              <br />
              {{ t('adminSupplier.modalWarning.actionWarning') }}
            </p>

            <div class="modal-actions">
              <button
                type="button"
                class="btn-modal-secondary"
                @click="showDeleteModal = false"
              >
                {{ t('adminSupplier.button.cancel') }}
              </button>
              <button
                type="button"
                class="btn-modal-danger"
                @click="confirmDelete"
              >
                {{ t('adminSupplier.button.confirm') }}
              </button>
            </div>
          </div>
        </div>
      </template>
    </template>
  </form>
</template>

<style scoped lang="scss">
.supplier-details-box {
  background-color: var(--admin-suppliers-green-bg);
  width: 100%;
  min-height: 40rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 1.5rem;
  border-radius: 4px;
  border: 2px solid var(--admin-suppliers-green-border);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
  font-size: 0.9rem;
}

.top-right-actions {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 10;
  display: flex;
  gap: 0.5rem;
}

.button.btn-edit,
.button.btn-cancel,
.button.btn-delete,
.btn-save {
  font-weight: 600;
  border-radius: 4px;
  padding: 0.5em 1em;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
  }

  &:active {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

.btn-cancel {
  margin-right: 10px;
}

.btn-edit,
.btn-delete {
  background-color: transparent;
  box-shadow: none;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

.btn-edit {
  border: 1px solid var(--admin-suppliers-action-blue);
  color: var(--admin-suppliers-action-blue);

  &:hover {
    background-color: color-mix(
      in srgb,
      var(--admin-suppliers-action-blue),
      transparent 90%
    );
    color: color-mix(in srgb, var(--admin-suppliers-action-blue), black 10%);
  }
}

.btn-delete {
  border: 1px solid var(--admin-suppliers-action-danger);
  color: var(--admin-suppliers-action-danger);

  &:hover {
    background-color: color-mix(
      in srgb,
      var(--admin-suppliers-action-danger),
      transparent 90%
    );
    color: color-mix(in srgb, var(--admin-suppliers-action-danger), black 10%);
    border-color: color-mix(
      in srgb,
      var(--admin-suppliers-action-danger),
      black 10%
    );
  }
}

.btn-save {
  background-color: var(--admin-suppliers-action-green);
  border: 1px solid var(--admin-suppliers-action-green-dark);
  color: white;

  &:hover {
    background-color: color-mix(
      in srgb,
      white 15%,
      var(--admin-suppliers-action-green)
    );
    border-color: color-mix(
      in srgb,
      white 15%,
      var(--admin-suppliers-action-green)
    );
    color: white;
  }
}

.btn-cancel {
  background-color: var(--admin-suppliers-action-grey-bg);
  border: 1px solid var(--admin-suppliers-action-grey-border);
  color: var(--admin-suppliers-action-grey-text);

  &:hover {
    background-color: #f8fafc;
    border-color: color-mix(
      in srgb,
      var(--admin-suppliers-action-grey-border),
      black 10%
    );
    color: color-mix(
      in srgb,
      var(--admin-suppliers-action-grey-text),
      black 10%
    );
  }
}

.strong-hr {
  border: none;
  height: 2px;
  width: 100%;
  background-color: var(--admin-suppliers-green-border);
  margin: 1.5rem 0;
}

.title.is-5 {
  font-size: 1.25rem;
  margin-bottom: 1rem;
}

.four-col-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 1.5rem 1rem;
  width: 100%;
}

.three-col-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1.5rem 1rem;
  width: 100%;
}

.grid-item {
  display: flex;
  flex-direction: column;
  gap: 0;
  align-items: flex-start;
  box-sizing: border-box;
  word-break: break-word;
  margin: 0;
  min-width: 200px;
}

.label {
  display: block;
  font-weight: bold;
  white-space: normal;
  text-align: left;
  margin-bottom: 0.25rem;
}

.helper-text {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.5rem;
  font-style: italic;
}

.checkbox-space {
  margin-left: 0.3rem;
}

.value {
  display: block;
  flex-grow: 1;
  word-break: break-word;
  min-width: 0;
  min-height: 1.5rem;
}

.is-active {
  color: var(--admin-suppliers-status-active);
  font-weight: bold;
}

.is-inactive {
  color: var(--admin-suppliers-status-inactive);
  font-weight: bold;
}

.edit-input {
  width: 100%;
  padding: 0.25rem 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9rem;
  background-color: #fff;
  height: 2rem;
  transition: border-color 0.3s;

  &:focus {
    border-color: var(--admin-suppliers-action-green-dark);
    box-shadow: 0 0 0 2px
      color-mix(
        in srgb,
        var(--admin-suppliers-action-green-dark),
        transparent 90%
      );
    outline: none;
  }
}

.grid-spacer {
  height: 1px;
  padding: 0;
  margin: 0;
  opacity: 0;
}

.small-supplier-invoice {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  font-size: 0.76rem;
  transition: font-size 0.3s;
}

.small-supplier-invoice.large-font {
  font-size: 1.1rem;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.modal-box {
  background: white;
  padding: 1.4rem;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  text-align: center;
  border: 1px solid #e2e8f0;
  animation: modalFadeIn 0.2s ease-out;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.modal-text {
  color: #64748b;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.btn-modal-secondary,
.btn-modal-danger {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-modal-secondary {
  background-color: #f1f5f9;
  color: #475569;

  &:hover {
    background-color: #e2e8f0;
    color: #1e293b;
  }
}

.btn-modal-danger {
  background-color: #ef4444;
  color: white;

  &:hover {
    background-color: #dc2626;
  }
}
</style>
